import {
  PerformanceReportingMetricsResponse,
  PerformanceReportingTrendsResponse,
  PerformanceReportingDimensionsResponse,
  PerformanceReportingTop5SummariesResponse,
  TrendValue,
} from '../../_types';
import {
  REASON_FOR_USE_OF_FORCE_VALUES,
  TYPE_OF_INCIDENT_VALUES,
} from './useOfForceLookups';
import { formatTooltipDescription } from '../../_constants';

/**
 * Type representing a Use of Force event record
 * 
 * This matches the structure of records in uof_events.json with additional
 * fields for OrgId and Reason. These records will be dynamically generated
 * and used as base data to calculate metrics.
 */
export interface UseOfForceEvent {
  OrgId: string; // UUID of the organization
  EventDate: string; // ISO date string (YYYY-MM-DD)
  OfficerCount: number; // Number of officers (1-3, leaning toward fewer)
  OfficerInjury: 'Y' | 'N'; // Whether any officer was injured
  FatalOfficerInjury: 'Y' | 'N'; // Whether any officer injury was fatal
  OfficerDeescalation: 'Y' | 'N'; // Whether deescalation was attempted
  SuspectCount: number; // Number of suspects (1-5, more suspects = more officers)
  SuspectInjury: 'Y' | 'N'; // Whether any suspect was injured
  FatalSuspectInjury: 'Y' | 'N'; // Whether any suspect injury was fatal
  TypeOfIncident: string | null; // Type of incident (from TYPE_OF_INCIDENT_VALUES)
  Reason: string; // Reason for use of force (from REASON_FOR_USE_OF_FORCE_VALUES)
}

/**
 * Weighted distribution for TypeOfIncident based on sample data analysis
 * Top incidents get higher weights
 */
const TYPE_OF_INCIDENT_WEIGHTS: Record<string, number> = {
  'Other': 158,
  'Traffic Stop': 66,
  'Domestic Disturbance': 54,
  'Mental Health': 47,
  'Suspicious Person': 39,
  'Assist Another LE Agency': 28,
  'Burglary': 26,
  'DUI': 25,
  'Public Intoxication': 21,
  'Domestic': 20,
  'Warrant': 15,
  'Trespass': 14,
  'Disorderly Conduct': 14,
  'Disturbance': 12,
  'Arrest Situation': 11,
  'Robbery': 9,
  'Intoxicated Person': 9,
  '72 Hour (Not Suicidal)': 9,
  'Suicidal': 9,
  'Felony Stop': 9,
};

// Default weight for incidents not in the top list
const DEFAULT_INCIDENT_WEIGHT = 1;

/**
 * Helper to select a random item from an array with weighted distribution
 */
function weightedRandom<T>(items: T[], weights: number[]): T {
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  let random = Math.random() * totalWeight;
  
  for (let i = 0; i < items.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return items[i];
    }
  }
  
  return items[items.length - 1];
}

/**
 * Helper to select a random item from an array
 */
function randomItem<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/**
 * Helper to generate a random date between start and end dates
 */
function randomDate(startDate: string, endDate: string): string {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const randomTime = start + Math.random() * (end - start);
  return new Date(randomTime).toISOString().split('T')[0];
}

/**
 * Helper to generate OfficerCount (1-3, heavily skewed to 1-2)
 * Based on sample data: 1 (55%), 2 (26%), 3 (11%), 4 (5%), 5 (2%)
 */
function generateOfficerCount(): number {
  const rand = Math.random();
  if (rand < 0.55) return 1;
  if (rand < 0.81) return 2;
  if (rand < 0.92) return 3;
  if (rand < 0.97) return 4;
  return 5;
}

/**
 * Helper to generate SuspectCount (1-5, heavily skewed to 1)
 * Based on sample data: 1 (87%), 2 (5%), 3 (1.4%), 4 (0.6%), 0 (6%)
 * Also considers OfficerCount - more officers = slightly higher chance of more suspects
 */
function generateSuspectCount(officerCount: number): number {
  const rand = Math.random();
  
  // If there are more officers (3+), slightly shift probability toward more suspects
  // by reducing the probability of single suspect and 0 suspects
  if (officerCount >= 3) {
    if (rand < 0.80) return 1; // Reduced from 87%
    if (rand < 0.88) return 2; // Increased from 5%
    if (rand < 0.95) return 3; // Increased from 1.4%
    if (rand < 0.98) return 4; // Increased from 0.6%
    if (rand < 0.995) return 0; // Reduced from 6%
    return 5;
  }
  
  // Standard distribution for 1-2 officers
  if (rand < 0.87) return 1;
  if (rand < 0.92) return 2;
  if (rand < 0.936) return 3;
  if (rand < 0.942) return 4;
  if (rand < 0.998) return 0; // Some events have 0 suspects
  return 5;
}

/**
 * Generate a single Use of Force event
 */
function generateEvent(
  orgId: string,
  startDate: string,
  endDate: string
): UseOfForceEvent {
  const officerCount = generateOfficerCount();
  const suspectCount = generateSuspectCount(officerCount);
  
  // Generate TypeOfIncident with weighted distribution
  const incidentWeights = TYPE_OF_INCIDENT_VALUES.map(
    (incident) => TYPE_OF_INCIDENT_WEIGHTS[incident] || DEFAULT_INCIDENT_WEIGHT
  );
  const typeOfIncident = Math.random() < 0.15 ? null : weightedRandom(
    Array.from(TYPE_OF_INCIDENT_VALUES),
    incidentWeights
  );
  
  // Generate Reason (equal probability for now, can be weighted later)
  const reason = randomItem(REASON_FOR_USE_OF_FORCE_VALUES);
  
  // OfficerInjury: 11% Y, 89% N
  const officerInjury: 'Y' | 'N' = Math.random() < 0.11 ? 'Y' : 'N';
  
  // FatalOfficerInjury: Higher chance for demo purposes (about 30-40% of injuries are fatal)
  // This gives roughly 3-4% of all events having fatal officer injuries
  const fatalOfficerInjury: 'Y' | 'N' =
    officerInjury === 'Y' && Math.random() < 0.35 ? 'Y' : 'N';
  
  // SuspectInjury: 23% Y, 77% N
  const suspectInjury: 'Y' | 'N' = Math.random() < 0.23 ? 'Y' : 'N';
  
  // FatalSuspectInjury: Higher chance for demo purposes (about 25-35% of injuries are fatal)
  // This gives roughly 6-8% of all events having fatal suspect injuries
  const fatalSuspectInjury: 'Y' | 'N' =
    suspectInjury === 'Y' && Math.random() < 0.30 ? 'Y' : 'N';
  
  // OfficerDeescalation: 45% Y, 55% N
  const officerDeescalation: 'Y' | 'N' = Math.random() < 0.45 ? 'Y' : 'N';
  
  return {
    OrgId: orgId,
    EventDate: randomDate(startDate, endDate),
    OfficerCount: officerCount,
    OfficerInjury: officerInjury,
    FatalOfficerInjury: fatalOfficerInjury,
    OfficerDeescalation: officerDeescalation,
    SuspectCount: suspectCount,
    SuspectInjury: suspectInjury,
    FatalSuspectInjury: fatalSuspectInjury,
    TypeOfIncident: typeOfIncident,
    Reason: reason,
  };
}

/**
 * Generate base Use of Force event data
 * 
 * Generates a collection of events with weighted randomization based on
 * patterns found in the sample data. The number of events is determined
 * by the date range - approximately 1-3 events per day on average.
 */
export function generateUseOfForceBaseData(
  orgId: string,
  startDate: string,
  endDate: string
): UseOfForceEvent[] {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  
  // Generate approximately 1-3 events per day on average
  const avgEventsPerDay = 1.5 + Math.random(); // 1.5 to 2.5
  const numEvents = Math.floor(daysDiff * avgEventsPerDay);
  
  const events: UseOfForceEvent[] = [];
  for (let i = 0; i < numEvents; i++) {
    events.push(generateEvent(orgId, startDate, endDate));
  }
  
  return events;
}

/**
 * Generator for Use of Force module data
 * 
 * This is a placeholder implementation that will be replaced with
 * actual mock data generation logic.
 */
export class UseOfForceGenerator {
  /**
   * Generate metrics for Use of Force module
   */
  static generateMetrics(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingMetricsResponse {
    const baseData = generateUseOfForceBaseData(orgId, startDate, endDate);
    
    // Total incidents is the count of events in the base data
    const totalIncidents = baseData.length;
    
    // Fatal incidents are events where either FatalOfficerInjury or FatalSuspectInjury is 'Y'
    const fatalIncidents = baseData.filter(
      (event) => event.FatalOfficerInjury === 'Y' || event.FatalSuspectInjury === 'Y'
    ).length;
    
    const percentFatalIncidents =
      totalIncidents > 0
        ? Math.round((fatalIncidents / totalIncidents) * 1000) / 1000
        : 0;
    
    const metrics: PerformanceReportingMetricsResponse = [
      {
        key: 'totalIncidents',
        label: 'Total UOF Incidents',
        value: totalIncidents,
        valueFormat: 'number',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description:
          'Total number of completed UOF incidents during the selected date range',
      },
      {
        key: 'percentFatalIncidents',
        label: 'Incidents with Fatalities',
        value: percentFatalIncidents,
        valueFormat: 'percent',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description: 'Percentage of incidents with fatalities',
      },
    ];
    
    return metrics;
  }

  /**
   * Calculate number of periods based on date range
   * If 31 days or less, use weeks; otherwise use months
   */
  private static calculatePeriods(
    startDate: string,
    endDate: string
  ): { useWeeks: boolean; periods: number } {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.floor(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    // If period is 31 days or less, use weeks
    const useWeeks = diffDays <= 31;

    if (useWeeks) {
      // Calculate number of weeks
      const weeks = Math.ceil(diffDays / 7);
      return { useWeeks: true, periods: Math.max(1, weeks) };
    }

    // For more than a month, calculate number of complete months
    const monthsDiff =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      1; // +1 to include both start and end months

    return { useWeeks: false, periods: Math.max(1, monthsDiff) };
  }

  /**
   * Get the period start date for a given period index
   */
  private static getPeriodStartDate(
    startDate: string,
    periodIndex: number,
    useWeeks: boolean
  ): Date {
    const start = new Date(startDate);
    if (useWeeks) {
      const periodDate = new Date(start);
      periodDate.setDate(start.getDate() + periodIndex * 7);
      return periodDate;
    } else {
      return new Date(start.getFullYear(), start.getMonth() + periodIndex, 1);
    }
  }

  /**
   * Determine which period an event belongs to
   */
  private static getEventPeriod(
    eventDate: string,
    startDate: string,
    useWeeks: boolean
  ): number {
    const event = new Date(eventDate);
    const start = new Date(startDate);

    if (useWeeks) {
      const diffDays = Math.floor(
        (event.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
      );
      return Math.floor(diffDays / 7);
    } else {
      const monthsDiff =
        (event.getFullYear() - start.getFullYear()) * 12 +
        (event.getMonth() - start.getMonth());
      return monthsDiff;
    }
  }

  /**
   * Generate trends data for Use of Force module
   */
  static generateTrends(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingTrendsResponse {
    const baseData = generateUseOfForceBaseData(orgId, startDate, endDate);
    const { useWeeks, periods } = UseOfForceGenerator.calculatePeriods(
      startDate,
      endDate
    );
    const periodMode = useWeeks ? 'week' : 'month';

    // Group events by period
    const periodData: Array<{
      total: number;
      fatal: number;
    }> = Array(periods).fill(null).map(() => ({ total: 0, fatal: 0 }));

    baseData.forEach((event) => {
      const periodIndex = UseOfForceGenerator.getEventPeriod(
        event.EventDate,
        startDate,
        useWeeks
      );

      // Only count events within the valid period range
      if (periodIndex >= 0 && periodIndex < periods) {
        periodData[periodIndex].total++;
        if (
          event.FatalOfficerInjury === 'Y' ||
          event.FatalSuspectInjury === 'Y'
        ) {
          periodData[periodIndex].fatal++;
        }
      }
    });

    // Build trend series data
    const totalData = periodData.map((data, index) => {
      const periodDate = UseOfForceGenerator.getPeriodStartDate(
        startDate,
        index,
        useWeeks
      );
      const period = periodDate.toISOString().split('T')[0];

      return {
        period,
        value: data.total,
        description: [
          `${data.total} incident${data.total === 1 ? '' : 's'}`,
          'Total UOF incidents',
        ],
      };
    });

    const fatalData = periodData.map((data, index) => {
      const periodDate = UseOfForceGenerator.getPeriodStartDate(
        startDate,
        index,
        useWeeks
      );
      const period = periodDate.toISOString().split('T')[0];

      return {
        period,
        value: data.fatal,
        description: [
          `${data.fatal} incident${data.fatal === 1 ? '' : 's'}`,
          'Incidents with fatalities',
        ],
      };
    });

    return {
      periodMode,
      series: [
        {
          name: 'total',
          data: totalData,
        },
        {
          name: 'fatalities',
          data: fatalData,
        },
      ],
    };
  }

  /**
   * Generate dimensions data for Use of Force module
   */
  static generateDimensions(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingDimensionsResponse {
    const baseData = generateUseOfForceBaseData(orgId, startDate, endDate);
    const dimensions: PerformanceReportingDimensionsResponse = [];
    const subject = 'incident';

    // Dimension 1: Reason For Force
    const reasonCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      reasonCounts[event.Reason] = (reasonCounts[event.Reason] || 0) + 1;
    });

    const totalReasons = baseData.length;
    const reasonData = Object.entries(reasonCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalReasons, subject),
      }))
      .sort((a, b) => b.value - a.value); // Sort descending

    dimensions.push({
      name: 'Reason For Force',
      data: reasonData,
    });

    return dimensions;
  }

  /**
   * Generate top-5 summaries for Use of Force module
   */
  static generateTop5Summaries(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingTop5SummariesResponse {
    const baseData = generateUseOfForceBaseData(orgId, startDate, endDate);
    const subject = 'incident';
    const summaries: PerformanceReportingTop5SummariesResponse = [];

    // Summary 1: Outcome of Force (5 simplified categories)
    let noInjury = 0;
    let suspectInjury = 0; // Any suspect injury (non-fatal)
    let officerInjury = 0; // Any officer injury (non-fatal)
    let suspectFatality = 0; // Any suspect fatality
    let officerFatality = 0; // Any officer fatality

    baseData.forEach((event) => {
      const hasSuspectInjury = event.SuspectInjury === 'Y' && event.FatalSuspectInjury === 'N';
      const hasSuspectFatality = event.FatalSuspectInjury === 'Y';
      const hasOfficerInjury = event.OfficerInjury === 'Y' && event.FatalOfficerInjury === 'N';
      const hasOfficerFatality = event.FatalOfficerInjury === 'Y';

      // Count incidents in each category (incidents can be in multiple categories)
      if (!hasSuspectInjury && !hasSuspectFatality && !hasOfficerInjury && !hasOfficerFatality) {
        noInjury++;
      }
      if (hasSuspectInjury) {
        suspectInjury++;
      }
      if (hasOfficerInjury) {
        officerInjury++;
      }
      if (hasSuspectFatality) {
        suspectFatality++;
      }
      if (hasOfficerFatality) {
        officerFatality++;
      }
    });

    const totalIncidents = baseData.length;

    const outcomeData = [
      {
        name: 'No Injury',
        value: noInjury,
        description: formatTooltipDescription(noInjury, totalIncidents, subject),
      },
      {
        name: 'Suspect Injury',
        value: suspectInjury,
        description: formatTooltipDescription(suspectInjury, totalIncidents, subject),
      },
      {
        name: 'Officer Injury',
        value: officerInjury,
        description: formatTooltipDescription(officerInjury, totalIncidents, subject),
      },
      {
        name: 'Suspect Fatality',
        value: suspectFatality,
        description: formatTooltipDescription(suspectFatality, totalIncidents, subject),
      },
      {
        name: 'Officer Fatality',
        value: officerFatality,
        description: formatTooltipDescription(officerFatality, totalIncidents, subject),
      },
    ].sort((a, b) => b.value - a.value); // Sort descending

    summaries.push({
      name: 'Outcome of Force',
      data: outcomeData,
    });

    // Summary 2: Incident Type (top 5 non-null TypeOfIncident values)
    const incidentTypeCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      if (event.TypeOfIncident !== null) {
        incidentTypeCounts[event.TypeOfIncident] =
          (incidentTypeCounts[event.TypeOfIncident] || 0) + 1;
      }
    });

    // Get total count for percentage calculation
    const totalIncidentTypes = Object.values(incidentTypeCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    // Sort by count descending and take top 5
    const top5IncidentTypes = Object.entries(incidentTypeCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalIncidentTypes, subject),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    summaries.push({
      name: 'Incident Type',
      data: top5IncidentTypes,
    });

    return summaries;
  }
}
